import React from 'react';
import { Table } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import { verifyValue } from '../../../specialCore';
import { calculateKroKrgIntersection } from './krIntersection';
import { CorrelationEndpointSource } from '../../../../../@types/projects/types';
import { resolveGasOilValues } from './valueResolver';

const columns = [
  {
    title: (
      <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
        Relative permeability endpoints
      </div>
    ),
    dataIndex: 'combined', // Dados combinados
    key: 'combined',
    render: (text: string) => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 8px'
        }}
      >
        <span>{text.split(' - ')[0]}</span>
        <span>{text.split(' - ')[1]}</span>
      </div>
    )
  }
];

const format4 = (value: number | string | null | undefined) => {
  if (typeof value === 'string') {
    return value;
  }
  return value == null || Number.isNaN(value)
    ? verifyValue(value as any)
    : value.toFixed(4);
};

const intersectionColumns = [
  {
    title: 'Sg',
    dataIndex: 'sg',
    key: 'sg',
    align: 'center' as const,
    render: (value: number | string | null) => format4(value)
  },
  {
    title: 'Kro=Krg',
    dataIndex: 'kroKrg',
    key: 'kroKrg',
    align: 'center' as const,
    render: (value: number | string | null) => format4(value)
  }
];

interface CoreyExponentsTableProps {
  endpointSource: CorrelationEndpointSource;
}

const CoreyExponentsTable: React.FC<CoreyExponentsTableProps> = ({
  endpointSource
}) => {
  const { selectedInfo } = useSelected();
  const sampleCurves = selectedInfo?.sample?.input_sample_curves?.gas_oil;
  const sgValues = sampleCurves?.sg_krel ?? [];
  const resolved = resolveGasOilValues(selectedInfo?.sample, endpointSource);

  const intersection = calculateKroKrgIntersection({
    sgValues,
    swi: resolved.swi ?? sgValues?.[0],
    sgc: resolved.sgc,
    sor: resolved.sor,
    no: resolved.no,
    ng: resolved.ng,
    krgEndPoint: resolved.krgEndpoint
  });

  const endpointLabelSuffix =
    endpointSource === 'refinement' ? 'refinement' : 'absolute';
  const data = [
    {
      key: '1',
      combined: `Swi - ${verifyValue(selectedInfo?.sample?.input_sample_values?.swi)}`
    },
    {
      key: '2',
      combined: `No - ${format4(resolved.no)}`
    },
    {
      key: '3',
      combined: `Ng - ${format4(resolved.ng)}`
    },
    {
      key: '4',
      combined: `Krg ${endpointLabelSuffix} - ${format4(resolved.krgEndpoint)}`
    },
    {
      key: '5',
      combined: `Kro ${endpointLabelSuffix} - ${format4(resolved.kroEndpoint)}`
    },
    {
      key: '6',
      combined: `Sgc - ${verifyValue(resolved.sgc)}`
    },
    {
      key: '7',
      combined: `Sorg${endpointSource === 'refinement' ? ' refinement' : ''} - ${format4(resolved.sor)}`
    }
  ];

  const intersectionDisplay =
    intersection.status === 'valid' ? null : 'Not available';
  const intersectionData = [
    {
      key: '1',
      sg:
        intersection.status === 'valid'
          ? intersection.intersectionSg
          : intersectionDisplay,
      kroKrg:
        intersection.status === 'valid'
          ? intersection.intersectionK
          : intersectionDisplay
    }
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        size="small"
        style={{ padding: '0 8px' }}
      />
      <Table
        columns={intersectionColumns}
        dataSource={intersectionData}
        pagination={false}
        size="small"
        style={{ padding: '0 8px', marginTop: '0.5rem' }}
        title={() => (
          <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
            Finding Sg @ Kro=Krg
          </div>
        )}
      />
    </div>
  );
};

export default CoreyExponentsTable;
